import React, { useMemo, useCallback, useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardAction,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DownloadIcon } from '@phosphor-icons/react';
import type {
  ReportTable as ReportTableType,
  ReportTimeFilter,
} from '@erp/shared-types';
import { TimeFilterPresetSelector } from '@/components/rebates/TimeFilterPreset';
import type { TimeFilterPreset } from '@/utils/timeFilterPresets';
import BreakdownTable, {
  type BreakdownCell,
  type BreakdownCellContext,
} from '@/components/common/breakdown-tables/BreakdownTable';
import Papa from 'papaparse';
import { formatCurrency } from '@erp/shared-utils';
import { T, useGT } from 'gt-react';

type SortDirection = 'asc' | 'desc' | null;

interface ReportTableProps {
  tableName?: string;
  table: ReportTableType;
  timeFilter: ReportTimeFilter;
  onTimeFilterChange: (filter: ReportTimeFilter) => void;
  onExport?: () => void;
  showTotals?: boolean;
  rowLabel?: string; // Label for the row dimension (default: "Category")
  showTimeFilter?: boolean; // Show time filter controls (default: true)
  renderCell?: (context: BreakdownCellContext) => React.ReactNode; // Custom cell renderer
  enableSorting?: boolean; // Enable column sorting (default: true)
  defaultSortColumn?: string; // Default column to sort by (e.g., "Dec" or last visible column)
  defaultSortDirection?: 'asc' | 'desc'; // Default sort direction (default: 'desc')
  headerActions?: React.ReactNode;
}

/**
 * Reusable component for displaying report tables with time filtering and sorting
 * Uses BreakdownTable component for consistent styling
 */
export const ReportTable: React.FC<ReportTableProps> = ({
  table,
  tableName,
  timeFilter,
  onTimeFilterChange,
  onExport,
  showTotals = true,
  rowLabel = 'Category',
  showTimeFilter = true,
  renderCell: customRenderCell,
  enableSorting = true,
  defaultSortColumn,
  defaultSortDirection = 'desc',
  headerActions,
}) => {
  const gt = useGT();
  // Use gt() (string) so BreakdownTable CSV export can read the row header via textForCsvExport.
  const rowLabelDisplay =
    rowLabel === 'Category'
      ? gt('Category', {
          context: 'report breakdown table, default row dimension column label',
        })
      : rowLabel;
  const monthColumnLabel = gt('Month', {
    context: 'report breakdown table, time period column header',
  });
  const [sortColumnIndex, setSortColumnIndex] = useState<number | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  // A month-based table has headers like "Jan 2024", "Feb 2024", etc. or just "Jan", "Feb", etc.
  const monthPatternWithYear = useMemo(
    () => /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{4}$/,
    [],
  );
  const monthPatternWithoutYear = useMemo(
    () => /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)$/,
    [],
  );
  const isMonthHeader = useCallback(
    (header: string): boolean => {
      return (
        monthPatternWithYear.test(header) ||
        monthPatternWithoutYear.test(header)
      );
    },
    [monthPatternWithYear, monthPatternWithoutYear],
  );
  const hasMonthHeaders = table.columnHeaders.some(isMonthHeader);
  const isMonthBasedTable = hasMonthHeaders;

  const visibleColumnIndices = useMemo(() => {
    if (!isMonthBasedTable) {
      return Array.from({ length: table.columnHeaders.length }, (_, i) => i);
    }

    const monthColumnIndices: number[] = [];
    const nonMonthColumnIndices: number[] = [];

    table.columnHeaders.forEach((header, index) => {
      if (isMonthHeader(header)) {
        monthColumnIndices.push(index);
      } else {
        nonMonthColumnIndices.push(index);
      }
    });

    const currentMonthIndex = new Date().getMonth();
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    let visibleMonthIndices: number[] = [];
    switch (timeFilter) {
      case 'ytd': {
        visibleMonthIndices = monthColumnIndices.filter((colIndex) => {
          const header = table.columnHeaders[colIndex];
          const monthMatch = header.match(
            /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/,
          );
          if (!monthMatch) return false;
          const monthName = monthMatch[1];
          const monthIdx = monthNames.indexOf(monthName);
          return monthIdx >= 0 && monthIdx <= currentMonthIndex;
        });
        break;
      }
      case 'current': {
        const currentMonthName = monthNames[currentMonthIndex];
        visibleMonthIndices = monthColumnIndices.filter((colIndex) => {
          const header = table.columnHeaders[colIndex];
          return header.startsWith(currentMonthName);
        });
        break;
      }
      case 'all':
      default: {
        visibleMonthIndices = monthColumnIndices;
        break;
      }
    }

    // Always include non-month columns (like Total YTD, Post Y/E Adj #1)
    const allVisibleIndices: number[] = [];
    table.columnHeaders.forEach((_, index) => {
      if (nonMonthColumnIndices.includes(index)) {
        allVisibleIndices.push(index);
      } else if (visibleMonthIndices.includes(index)) {
        allVisibleIndices.push(index);
      }
    });

    return allVisibleIndices;
  }, [isMonthBasedTable, isMonthHeader, table.columnHeaders, timeFilter]);

  // Custom cell renderer that colors based on lineType and uses formattedValue from backend
  const defaultRenderCell = useCallback(
    (context: BreakdownCellContext): React.ReactNode => {
      const { cell, column } = context;
      const value = cell.value;
      const metadata = cell.metadata as
        | {
            lineType?: string;
            isFutureMonth?: boolean;
            formattedValue?: string;
          }
        | undefined;

      // Handle null/undefined values
      if (value === null || value === undefined) {
        return <span className="text-xs text-muted-foreground">-</span>;
      }

      // Handle "Supplier Code" and "Division Code" columns as text (not currency)
      if (column === 'Supplier Code' || column === 'Division Code') {
        const codeValue = metadata?.formattedValue || '';
        if (!codeValue) {
          return <span className="text-xs text-muted-foreground">-</span>;
        }
        return (
          <div className="text-xs font-medium text-foreground">{codeValue}</div>
        );
      }

      // Determine color based on lineType
      let textColor = 'text-foreground'; // Default
      if (metadata?.lineType === 'reported') {
        textColor = 'text-black'; // Black for reported
      } else if (metadata?.lineType === 'predictions') {
        textColor = 'text-blue-600'; // Blue for predictions
      } else if (metadata?.lineType === 'estimates') {
        textColor = 'text-green-600'; // Green for estimates
      } else if (metadata?.lineType === 'payment') {
        textColor = 'text-purple-600'; // Purple for payment allocations (closed agreements)
      } else if (metadata?.lineType === 'adjustment') {
        textColor = 'text-purple-600'; // Purple for PYE adjustments
      }

      // Use formattedValue from backend if available, otherwise format on frontend
      const displayValue =
        metadata?.formattedValue ??
        formatCurrency(typeof value === 'number' ? value : 0);

      return (
        <div className={`text-xs font-medium ${textColor}`}>{displayValue}</div>
      );
    },
    [],
  );

  // Use custom renderCell if provided, otherwise use default
  const renderCell = customRenderCell || defaultRenderCell;

  React.useEffect(() => {
    if (defaultSortColumn && sortColumnIndex === null && enableSorting) {
      const columnIndex = table.columnHeaders.indexOf(defaultSortColumn);
      if (columnIndex !== -1) {
        setSortColumnIndex(columnIndex);
        setSortDirection(defaultSortDirection);
      } else if (visibleColumnIndices.length > 0) {
        // Default to last visible column
        const lastVisibleIndex =
          visibleColumnIndices[visibleColumnIndices.length - 1];
        setSortColumnIndex(lastVisibleIndex);
        setSortDirection(defaultSortDirection);
      }
    }
  }, [
    defaultSortColumn,
    defaultSortDirection,
    enableSorting,
    table.columnHeaders,
    visibleColumnIndices,
    sortColumnIndex,
  ]);

  // Total row (id === 'total') is excluded from sorting and always stays at the bottom
  const sortedRows = useMemo(() => {
    const totalRow = table.rows.find((row) => row.id === 'total');
    const dataRows = table.rows.filter((row) => row.id !== 'total');

    if (!enableSorting || sortColumnIndex === null || sortDirection === null) {
      return totalRow ? [...dataRows, totalRow] : dataRows;
    }

    const columnHeader = table.columnHeaders[sortColumnIndex];
    const isCodeColumn =
      columnHeader === 'Division Code' || columnHeader === 'Supplier Code';

    const sorted = [...dataRows].sort((a, b) => {
      const aCell = a.cells[sortColumnIndex];
      const bCell = b.cells[sortColumnIndex];

      // For code columns, use formattedValue for string comparison
      if (isCodeColumn) {
        const aStr = aCell?.formattedValue ?? '';
        const bStr = bCell?.formattedValue ?? '';
        const comparison = aStr.localeCompare(bStr, undefined, {
          sensitivity: 'base',
        });
        return sortDirection === 'asc' ? comparison : -comparison;
      }

      const aValue = aCell?.value ?? 0;
      const bValue = bCell?.value ?? 0;
      const aNum = typeof aValue === 'number' ? aValue : 0;
      const bNum = typeof bValue === 'number' ? bValue : 0;

      return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
    });

    // Append total row at the end if it exists
    return totalRow ? [...sorted, totalRow] : sorted;
  }, [
    table.rows,
    sortColumnIndex,
    sortDirection,
    enableSorting,
    table.columnHeaders,
  ]);

  const handleSortChange = useCallback(
    (columnHeader: string) => {
      const columnIndex = table.columnHeaders.indexOf(columnHeader);
      if (columnIndex === -1) return;

      if (sortColumnIndex === columnIndex) {
        // Toggle direction or clear sort
        if (sortDirection === 'desc') {
          setSortDirection('asc');
        } else if (sortDirection === 'asc') {
          setSortDirection(null);
          setSortColumnIndex(null);
        }
      } else {
        // Sort by new column (desc by default)
        setSortColumnIndex(columnIndex);
        setSortDirection('desc');
      }
    },
    [sortColumnIndex, sortDirection, table.columnHeaders],
  );

  const breakdownData = useMemo<BreakdownCell[]>(() => {
    const cells: BreakdownCell[] = [];

    sortedRows.forEach((row) => {
      visibleColumnIndices.forEach((columnIndex) => {
        const cell = row.cells[columnIndex];
        const metadata: Record<string, unknown> = {};
        if (cell?.lineType) {
          metadata.lineType = cell.lineType;
        }
        if (cell?.isFutureMonth !== undefined) {
          metadata.isFutureMonth = cell.isFutureMonth;
        }
        if (cell?.formattedValue) {
          metadata.formattedValue = cell.formattedValue;
        }
        // Always include cells, even if they're 0 or undefined (will show as 0 or -)
        cells.push({
          column: table.columnHeaders[columnIndex],
          subColumn: null,
          row: row.label,
          subRow: null,
          value: cell?.value ?? 0,
          metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
        });
      });
    });

    return cells;
  }, [sortedRows, table.columnHeaders, visibleColumnIndices]);

  const columnOrder = useMemo(() => {
    return visibleColumnIndices.map((index) => table.columnHeaders[index]);
  }, [visibleColumnIndices, table.columnHeaders]);

  const rowOrder = useMemo(() => {
    return sortedRows.map((row) => row.label);
  }, [sortedRows]);

  const handleExportCSV = () => {
    if (onExport) {
      onExport();
      return;
    }

    const formatValue = (value: number | string | null | undefined): string => {
      if (value === null || value === undefined) return '0.00';
      if (typeof value === 'number') {
        return value.toFixed(2);
      }
      const parsed = parseFloat(value);
      if (!isNaN(parsed)) {
        return parsed.toFixed(2);
      }
      return value;
    };

    try {
      const csvRows: string[][] = [];

      const headerRow: string[] = [rowLabel, ...columnOrder];
      csvRows.push(headerRow);

      sortedRows.forEach((row) => {
        const rowData: string[] = [row.label];

        visibleColumnIndices.forEach((index) => {
          const cell = row.cells[index];
          rowData.push(cell ? formatValue(cell.value) : '0');
        });

        csvRows.push(rowData);
      });

      if (showTotals) {
        const columnTotalsRow: string[] = ['Total'];
        visibleColumnIndices.forEach((index) => {
          const columnTotal = sortedRows.reduce(
            (sum, row) => sum + (row.cells[index]?.value || 0),
            0,
          );
          columnTotalsRow.push(formatValue(columnTotal));
        });
        csvRows.push(columnTotalsRow);
      }

      const csvContent = Papa.unparse(csvRows);

      const timestamp = new Date().toISOString().split('T')[0];
      const baseFilename = table.title
        ? table.title.toLowerCase().replace(/\s+/g, '-')
        : 'report-table';
      const downloadFilename = `${baseFilename}-${timestamp}.csv`;

      const blob = new Blob([csvContent], {
        type: 'text/csv;charset=utf-8;',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting CSV:', error);
    }
  };

  const sortColumnName =
    sortColumnIndex !== null ? table.columnHeaders[sortColumnIndex] : null;

  const hasLineTypes = useMemo(() => {
    const lineTypes = new Set<string>();
    table.rows.forEach((row) => {
      row.cells.forEach((cell) => {
        if (cell.lineType) {
          lineTypes.add(cell.lineType);
        }
      });
    });
    return {
      hasReported: lineTypes.has('reported'),
      hasPredictions: lineTypes.has('predictions'),
      hasEstimates: lineTypes.has('estimates'),
      hasPayment: lineTypes.has('payment'),
      hasAdjustment: lineTypes.has('adjustment'),
    };
  }, [table.rows]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-1">
            <CardTitle className="text-lg font-medium">
              {tableName ? tableName : table.title}
            </CardTitle>
          </div>
          <div className="flex items-center gap-3">
            {showTimeFilter && (
              <TimeFilterPresetSelector
                value={timeFilter as TimeFilterPreset}
                onChange={(preset) =>
                  onTimeFilterChange(preset as ReportTimeFilter)
                }
                visiblePresets={['ytd', 'all', 'current']}
              />
            )}
            {headerActions}
            {/* Color Legend */}
            {(hasLineTypes.hasReported ||
              hasLineTypes.hasPredictions ||
              hasLineTypes.hasEstimates ||
              hasLineTypes.hasPayment ||
              hasLineTypes.hasAdjustment) && (
              <div className="flex flex-wrap items-center gap-3 text-xs">
                <span className="text-muted-foreground font-medium">
                  <T context="rebate report chart color legend heading">
                    Legend:
                  </T>
                </span>
                {hasLineTypes.hasReported && (
                  <div className="flex items-center gap-1.5">
                    <div className="h-3 w-3 rounded bg-black" />
                    <span className="text-foreground">
                      <T context="rebate report legend, reported actuals">
                        Reported
                      </T>
                    </span>
                  </div>
                )}
                {hasLineTypes.hasPredictions && (
                  <div className="flex items-center gap-1.5">
                    <div className="h-3 w-3 rounded bg-blue-600" />
                    <span className="text-foreground">
                      <T context="rebate report legend, forecasted values">
                        Forecasted
                      </T>
                    </span>
                  </div>
                )}
                {hasLineTypes.hasEstimates && (
                  <div className="flex items-center gap-1.5">
                    <div className="h-3 w-3 rounded bg-green-600" />
                    <span className="text-foreground">
                      <T context="rebate report legend, estimated values">
                        Estimates
                      </T>
                    </span>
                  </div>
                )}
                {hasLineTypes.hasPayment && (
                  <div className="flex items-center gap-1.5">
                    <div className="h-3 w-3 rounded bg-purple-600" />
                    <span className="text-foreground">
                      <T context="rebate report legend, payment line">
                        Payment
                      </T>
                    </span>
                  </div>
                )}
                {hasLineTypes.hasAdjustment && (
                  <div className="flex items-center gap-1.5">
                    <div className="h-3 w-3 rounded bg-purple-600" />
                    <span className="text-foreground">
                      <T context="rebate report legend, adjustment line">
                        Adjustment
                      </T>
                    </span>
                  </div>
                )}
              </div>
            )}
            <CardAction>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportCSV}
                className="h-8 px-2"
              >
                <DownloadIcon className="h-4 w-4" />
              </Button>
            </CardAction>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <BreakdownTable
          data={breakdownData}
          title=""
          showCard={false}
          rowLabel={rowLabelDisplay}
          columnLabel={monthColumnLabel}
          showXDim1Total={false}
          showYDim1Total={showTotals}
          xDimOrder={columnOrder}
          yDimOrder={rowOrder}
          maxHeight={700}
          fillMissingSubColumns={true}
          fillMissingSubRows={true}
          renderCell={renderCell}
          onColumnHeaderClick={enableSorting ? handleSortChange : undefined}
          sortColumn={sortColumnName}
          sortDirection={sortDirection}
        />
      </CardContent>
    </Card>
  );
};
